import React, { useState, useRef, useEffect } from 'react';
import { Zap, Send, X, Loader2, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { modelServicePost } from '../../utils/modelService';

// Markdown to HTML — tight spacing, no excessive gaps
const renderMd = (text, dk) => {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gm, `<h4 class="font-dm-serif text-sm mt-2 mb-0.5 ${dk ? 'text-gray-200' : 'text-gray-700'}">$1</h4>`)
    .replace(/^## (.*$)/gm, `<h3 class="font-dm-serif text-[15px] mt-3 mb-1 ${dk ? 'text-gray-100' : 'text-gray-800'}">$1</h3>`)
    .replace(/^# (.*$)/gm, `<h2 class="font-dm-serif text-base mt-3 mb-1 ${dk ? 'text-gray-100' : 'text-gray-800'}">$1</h2>`)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^> (.*$)/gm, `<div class="border-l-2 ${dk ? 'border-blue-500/50 bg-blue-900/10' : 'border-blue-400 bg-blue-50'} pl-2 py-0.5 my-1 text-[12px] rounded-r">$1</div>`)
    .replace(/^---$/gm, `<hr class="my-2 ${dk ? 'border-gray-700' : 'border-gray-200'}" />`)
    .replace(/^[•\-\*] (.*$)/gm, '<li class="ml-3 list-disc text-[12px] leading-snug">$1</li>')
    .replace(/^\d+\. (.*$)/gm, '<li class="ml-3 list-decimal text-[12px] leading-snug">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (m) => `<ul class="my-1 space-y-0.5">${m}</ul>`)
    // Tables
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(Boolean).map((c) => c.trim());
      if (cells.every((c) => /^[-:]+$/.test(c))) return '';
      return '<tr>' + cells.map((c) => `<td class="px-2 py-0.5 border ${dk ? 'border-gray-600' : 'border-gray-200'} text-[11px]">${c}</td>`).join('') + '</tr>';
    })
    .replace(/(<tr>.*<\/tr>\n?)+/g, (m) => `<table class="w-full my-1.5 border-collapse">${m}</table>`)
    // Paragraphs — convert double newlines to single break, single newlines to nothing (within same paragraph)
    .replace(/\n\n/g, '</p><p class="mt-1.5">')
    .replace(/\n/g, ' ')
    .replace(/^/, '<p class="mt-0">')
    .replace(/$/, '</p>')
    // Clean up empty paragraphs
    .replace(/<p class="mt-[^"]*"><\/p>/g, '');
};

const AskJPro = ({ portfolioData, propertyData, mode = 'portfolio', audience = 'internal', onClose }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content: string }
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const ask = async (question) => {
    const q = question || input.trim();
    if (!q && messages.length > 0) return;

    // Add user message to thread
    const userMsg = q || 'Analyze this portfolio';
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const endpoint = mode === 'property'
        ? '/audit/ai/property-insights'
        : mode === 'client-portfolio'
        ? '/audit/ai/client-portfolio-insights'
        : mode === 'director'
        ? '/audit/ai/director-insights'
        : '/audit/ai/portfolio-insights';
      const dataKey = mode === 'property' ? 'property_data' : mode === 'director' ? 'dashboard_data' : 'portfolio_data';
      const dataVal = mode === 'property' ? propertyData : portfolioData;

      // Include recent chat history as context
      const historyContext = newMessages.slice(-6).map((m) => `${m.role === 'user' ? 'User' : 'JPro'}: ${m.content}`).join('\n');

      const res = await modelServicePost(endpoint, {
        [dataKey]: dataVal,
        question: `${historyContext}\n\nUser: ${userMsg}`,
        audience,
      });

      if (res.ok) {
        const data = await res.json();
        setMessages([...newMessages, { role: 'assistant', content: data.insight }]);
      } else {
        setMessages([...newMessages, { role: 'assistant', content: 'Unable to generate insights right now. Please try again.' }]);
      }
    } catch {
      setMessages([...newMessages, { role: 'assistant', content: 'Unable to connect to JPro. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = mode === 'property'
    ? ['What should we do next?', 'Why is this delayed?', 'Summarize corrections']
    : mode === 'director'
    ? ['Who should I call this month?', 'Which lapsing clients are worth saving?', 'How is turnaround trending?', 'Is reviewer workload balanced?']
    : mode === 'client-portfolio'
    ? ['How is this client doing overall?', 'Compare audit years — any trends?', 'Where are the biggest bottlenecks?', 'Is the team capacity balanced?']
    : audience === 'client'
    ? ['Which properties need attention?', 'Are we on track?', 'What are the bottlenecks?']
    : ['Which properties need immediate attention?', 'How is the team workload?', 'What are the biggest risks?'];

  return (
    <div className={`rounded-xl border overflow-hidden flex flex-col ${d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-lg`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-2.5 shrink-0 ${d ? 'bg-gradient-to-r from-blue-900/30 to-violet-900/30 border-b border-gray-700' : 'bg-gradient-to-r from-blue-50 to-violet-50 border-b border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-blue-500" />
          <span className={`text-[13px] font-dm-serif ${d ? 'text-gray-200' : 'text-gray-700'}`}>Ask JPro</span>
        </div>
        {onClose && (
          <button onClick={onClose} className={`p-1 rounded ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Messages thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 max-h-[400px] min-h-[120px]">
        {messages.length === 0 && !loading ? (
          // Suggested questions
          <div className="space-y-1.5">
            <p className={`text-[12px] font-rasa mb-2 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
              {mode === 'director' ? 'Ask for a briefing — retention, at-risk clients, turnaround, workload' : mode === 'client-portfolio' ? 'Ask about this client\'s full portfolio across all years' : audience === 'client' ? 'Ask about your audit portfolio' : 'Ask about audits, properties, or team workload'}
            </p>
            {suggestedQuestions.map((q) => (
              <button
                key={q}
                onClick={() => ask(q)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[12px] font-rasa transition-colors ${
                  d ? 'text-gray-300 hover:bg-gray-700/50 border border-gray-700' : 'text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {q}
              </button>
            ))}
          </div>
        ) : (
          // Thread
          <div className="space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
                {msg.role === 'user' ? (
                  <div className={`inline-block px-3 py-1.5 rounded-2xl rounded-br-sm text-[13px] font-rasa max-w-[85%] ${
                    d ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  }`}>
                    {msg.content}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className={`shrink-0 mt-1 w-5 h-5 rounded-full flex items-center justify-center ${d ? 'bg-violet-900/40' : 'bg-violet-100'}`}>
                      <Sparkles size={10} className="text-violet-500" />
                    </div>
                    <div className={`flex-1 text-[13px] font-rasa leading-relaxed ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                      <div dangerouslySetInnerHTML={{ __html: renderMd(msg.content, d) }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className={`shrink-0 mt-1 w-5 h-5 rounded-full flex items-center justify-center ${d ? 'bg-violet-900/40' : 'bg-violet-100'}`}>
                  <Loader2 size={10} className="animate-spin text-violet-500" />
                </div>
                <span className={`text-[12px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>JPro is thinking...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`flex gap-2 px-4 py-2.5 border-t shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !loading && ask()}
          placeholder={mode === 'property' ? 'Ask about this property...' : mode === 'director' ? 'Ask about clients, retention, turnaround...' : mode === 'client-portfolio' ? 'Ask about this client\'s portfolio...' : 'Ask about your portfolio...'}
          className={`flex-1 px-3 py-2 rounded-lg border text-[13px] font-rasa ${
            d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-700 placeholder-gray-400'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        />
        <button
          onClick={() => ask()}
          disabled={(!input.trim() && messages.length === 0) || loading}
          className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
};

export default AskJPro;
